/**
 * Arrivée imprévue — the person visited answers from the web console.
 *
 * The desk (/poste) pages the host; the mobile app has a takeover for it, but
 * most office employees have no app, so without this page every walk-in for
 * them ended « Sans réponse ». Reached from the bell row
 * (`notificationTarget`) and from the « Répondre » button of the walk-in
 * email; a signed-out click goes through sign-in and comes back here
 * (`_dashboard.tsx` → `/sign-in?redirect=`).
 *
 * `spotArrival.get` / `respond` are NOT scoped to the active org (the
 * entitlement is the unit's live household), so the page words itself with
 * the REQUEST's org type, never `useTerms()`.
 *
 * Errors are split by what they let the person conclude, as on mobile:
 * NOT_FOUND is the server's verdict (gone, or never theirs); anything else is
 * a lost request, which keeps showing the row already held and self-heals on
 * the next poll.
 */
import type { SpotArrivalDecision } from "@gately/validators";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import { Button } from "@gately/ui/button";
import { StatusBadge } from "@gately/ui/status-badge";
import { getTerms } from "@gately/validators";

import {
  arrivalAnswerView,
  arrivalHeadline,
  arrivalTimeLabel,
  isLiveArrivalStatus,
  respondErrorMessage,
} from "~/lib/arrival-answer";
import { orpc } from "~/lib/orpc";
import { isBadRequestError, isNotFoundError } from "~/lib/orpc-error";

export const Route = createFileRoute("/_dashboard/arrivee/$id")({
  component: ArrivalAnswerPage,
});

/** The desk closes a request within seconds; 5 s keeps the page honest
 *  without hammering the API from a tab left open. */
const POLL_MS = 5000;

const TONE_BADGE = {
  waiting: "warning",
  success: "success",
  danger: "danger",
  neutral: "neutral",
} as const;

function ArrivalAnswerPage() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();

  const query = useQuery({
    ...orpc.spotArrival.get.queryOptions({ input: { id } }),
    refetchInterval: (q) =>
      q.state.data && isLiveArrivalStatus(q.state.data.status)
        ? POLL_MS
        : false,
    // A 404 is a verdict; retrying it only delays the message.
    retry: (count, error) => !isNotFoundError(error) && count < 2,
  });

  // Returned from the callbacks so `respond.isPending` stays true through the
  // refetch: the buttons never flash live between the answer landing and the
  // new row arriving, which is long enough for a doomed second click.
  const refetch = () =>
    queryClient.invalidateQueries({ queryKey: orpc.spotArrival.get.key() });

  const respond = useMutation(
    orpc.spotArrival.respond.mutationOptions({
      onSuccess: refetch,
      // Already handled / gone: the refetched row (or its 404) explains
      // itself. A network failure leaves the buttons live — clicking again IS
      // the retry.
      onError: (error) =>
        isBadRequestError(error) || isNotFoundError(error)
          ? refetch()
          : undefined,
    }),
  );

  const decide = (decision: SpotArrivalDecision) => {
    if (respond.isPending) return;
    respond.mutate({ id, decision });
  };

  const data = query.data;

  if (!data) {
    if (query.isPending) {
      return (
        <Frame>
          <p className="text-muted-foreground text-sm">Chargement…</p>
        </Frame>
      );
    }
    return (
      <Frame>
        <h1 className="text-foreground text-xl font-semibold">
          {isNotFoundError(query.error)
            ? "Demande introuvable"
            : "Impossible de charger la demande"}
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">
          {isNotFoundError(query.error)
            ? "Cette demande n'existe plus ou ne vous concerne pas."
            : "Vérifiez votre connexion, puis réessayez."}
        </p>
        <div className="mt-5 flex gap-3">
          {isNotFoundError(query.error) ? null : (
            <Button variant="outline" onClick={() => void query.refetch()}>
              Réessayer
            </Button>
          )}
          <Button variant="ghost" asChild>
            <Link to="/">Retour à l'accueil</Link>
          </Button>
        </div>
      </Frame>
    );
  }

  const terms = getTerms(data.orgType);
  const view = arrivalAnswerView(data, terms);

  return (
    <Frame>
      <p className="text-muted-foreground font-mono text-xs tracking-[0.14em] uppercase">
        Arrivée imprévue · {data.orgName}
      </p>
      <h1 className="text-foreground mt-2 text-2xl font-semibold tracking-[-0.02em]">
        {arrivalHeadline(data.visitorName, terms)}
      </h1>

      <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
        <dt className="text-muted-foreground">{terms.unitRowLabel}</dt>
        <dd className="text-foreground font-medium">{data.unitLabel}</dd>
        <dt className="text-muted-foreground">Motif</dt>
        <dd className="text-foreground font-medium">{data.purposeLabel}</dd>
        <dt className="text-muted-foreground">Heure</dt>
        <dd className="text-foreground font-medium">
          {arrivalTimeLabel(data.createdAt, data.timeZone)}
        </dd>
      </dl>

      <div className="mt-6 border-t pt-5">
        <StatusBadge tone={TONE_BADGE[view.tone]}>{view.label}</StatusBadge>
        {view.detail ? (
          <p className="text-muted-foreground mt-2 text-sm">{view.detail}</p>
        ) : null}
      </div>

      {view.actions.length > 0 ? (
        <div className="mt-5 flex flex-wrap gap-3">
          {view.actions.map((a) => (
            <Button
              key={a.decision}
              variant={
                a.decision === "approved"
                  ? "default"
                  : a.decision === "rejected"
                    ? "destructive"
                    : "outline"
              }
              disabled={respond.isPending}
              onClick={() => decide(a.decision)}
            >
              {a.label}
            </Button>
          ))}
        </div>
      ) : null}

      {respond.isError ? (
        <p role="alert" className="text-destructive mt-3 text-sm">
          {respondErrorMessage(respond.error)}
        </p>
      ) : null}
    </Frame>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-7 py-6">
      <div className="bg-card mx-auto max-w-xl rounded-xl border p-6 sm:p-8">
        {children}
      </div>
    </div>
  );
}
